'use client';

import { abortableSleep } from '@fpo3/ts-commons-core/abortable-sleep';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type QueryCacheConfig, readCache, writeCache } from './query-cache-helpers';
import type { SyncCache } from './sync-cache';
import { useQueryRevalidation } from './use-query-revalidation';
import { useSyncRef } from './use-sync-ref';
export interface UseServiceQueryOptions<T = unknown> {
  enabled?: boolean;
  revalidateOnFocus?: boolean;
  cache?: QueryCacheConfig;
  syncCache?: SyncCache<T>;
}

/**
 * Where the currently-displayed `data` originated from.
 *
 * - `'none'`: no data yet (initial state, query disabled, or cacheKey null).
 * - `'local'`: Phase 0 synchronous localStorage hit (instant render).
 * - `'cache'`: Phase 1 Cache-API hit (stale-while-revalidate).
 * - `'memory'`: Phase 1b in-memory `dataReference` fallback (cache miss but
 *   prior fetch result still in memory). Disambiguates "user already saw this
 *   data" from "fresh from network."
 * - `'network'`: Phase 3 network fetch resolved.
 */
export type UseServiceQuerySource = 'none' | 'local' | 'cache' | 'memory' | 'network';
export interface UseServiceQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** True when revalidating with existing cached data visible. */
  refreshing: boolean;
  /** Where `data` originated from. See `UseServiceQuerySource`. */
  source: UseServiceQuerySource;
  refetch: () => void;
}

/**
 * Like useApiQuery, but fetches data by calling a service method directly
 * instead of fetching a URL. Supports stale-while-revalidate via Cache API.
 *
 * @param cacheKey Stable string identifying this query (used for caching and stale-request tracking)
 * @param fetcher Async function that calls the service and returns the data
 * @param options Caching, enabled, revalidation settings, and optional syncCache for Phase 0 synchronous localStorage reads
 */
export function useServiceQuery<T>(cacheKey: string | null, fetcher: () => Promise<T>, options: UseServiceQueryOptions<T> = {}): UseServiceQueryResult<T> {
  const {
    enabled = true,
    revalidateOnFocus = false,
    cache,
    syncCache
  } = options;

  // Phase 0: synchronous localStorage read for state initialization.
  // Lazy initializer ensures syncCache.read() runs only on the first render,
  // avoiding wasted JSON.parse calls on every subsequent render.
  const [dataState, setDataState] = useState<{
    data: T | null;
    source: UseServiceQuerySource;
  }>(() => {
    const hit = syncCache && enabled && cacheKey != null ? syncCache.read() : null;
    return hit != null ? {
      data: hit,
      source: 'local'
    } : {
      data: null,
      source: 'none'
    };
  });
  const [loading, setLoading] = useState(() => dataState.source === 'local' ? false : enabled && cacheKey != null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const fetchIdReference = useRef(0);
  const fetcherReference = useSyncRef(fetcher);
  const cacheReference = useSyncRef(cache);
  const syncCacheReference = useSyncRef(syncCache);
  // Always-current dataState ref so doFetch reads the latest source/data
  // without capturing a stale closure (doFetch deps are [cacheKey, enabled]).
  const dataStateReference = useSyncRef(dataState);
  // Track current data for SWR: preserves in-memory data during refetches
  // even when the Cache API misses (e.g., fire-and-forget write race).
  // Keyed by cacheKey to avoid showing stale data from a different query.
  const dataReference = useRef<{
    key: string | null;
    value: T | null;
  }>({
    key: null,
    value: null
  });

  // Re-seed from syncCache when cacheKey changes (e.g., user identity swap).
  // The lazy useState initializer only runs on mount; this effect covers
  // subsequent key transitions so Phase 0 instant render applies to each key.
  const prevCacheKeyRef = useRef(cacheKey);
  useEffect(() => {
    if (cacheKey === prevCacheKeyRef.current) return;
    prevCacheKeyRef.current = cacheKey;
    const sc = syncCacheReference.current;
    if (sc && enabled && cacheKey != null) {
      const hit_0 = sc.read();
      if (hit_0 != null) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: re-seeding Phase 0 data when cacheKey changes; the lazy useState initializer only fires on mount
        setDataState({
          data: hit_0,
          source: 'local'
        });
        setLoading(false);
        return;
      }
    }
  }, [cacheKey, enabled]);

  /** Runs all fetch phases (local re-seed, cache, memory fallback, network) and writes results back to caches. */
  const doFetch = useCallback(async () => {
    if (!enabled || cacheKey == null) {
      fetchIdReference.current += 1;
      setLoading(false);
      setRefreshing(false);
      setError(null);
      setDataState({
        data: null,
        source: 'none'
      });
      dataReference.current = {
        key: null,
        value: null
      };
      return;
    }
    const fetchId = ++fetchIdReference.current;
    const cacheConfig = cacheReference.current;
    const syncCacheRef = syncCacheReference.current;
    let hadStaleData = false;

    // Phase 0b: if Phase 0 provided local data on mount, treat it as stale.
    // Read from ref so we always see the current source, not a stale closure.
    const currentDataState = dataStateReference.current;
    if (currentDataState.source === 'local' && currentDataState.data != null) {
      hadStaleData = true;
      dataReference.current = {
        key: cacheKey,
        value: currentDataState.data
      };
      setRefreshing(true);
    }

    // Phase 1: Read from cache
    if (cacheConfig) {
      const cached = await readCache(cacheConfig.cacheName, cacheKey);
      if (fetchIdReference.current !== fetchId) return;
      if (cached !== undefined) {
        hadStaleData = true;
        setDataState({
          data: cached as T,
          source: 'cache'
        });
        dataReference.current = {
          key: cacheKey,
          value: cached as T
        };
        setLoading(false);
        setRefreshing(true);
      }
    }

    // Phase 1b: Fall back to in-memory data when cache misses.
    // This prevents a visible reload when refetch() is called shortly after
    // a successful fetch (e.g., the fire-and-forget cache write hasn't
    // flushed yet). Only applies when the cached data is for the same key
    // (same query), not when navigating to a different query.
    if (!hadStaleData && dataReference.current.value != null && dataReference.current.key === cacheKey) {
      hadStaleData = true;
      setDataState({
        data: dataReference.current.value,
        source: 'memory'
      });
      setLoading(false);
      setRefreshing(true);
    }
    if (!hadStaleData) {
      setLoading(true);
      setDataState({
        data: null,
        source: 'none'
      });
      dataReference.current = {
        key: null,
        value: null
      };
      setRefreshing(false);
    }
    setError(null);

    // Phase 2: Delay if stale data shown with revalidateDelay
    if (hadStaleData && cacheConfig?.revalidateDelay) {
      await abortableSleep(cacheConfig.revalidateDelay, undefined);
      if (fetchIdReference.current !== fetchId) return;
    }

    // Phase 3: Fetch via service
    try {
      const result = await fetcherReference.current();
      if (fetchIdReference.current !== fetchId) return;
      setDataState({
        data: result,
        source: 'network'
      });
      dataReference.current = {
        key: cacheKey,
        value: result
      };

      // Write to caches
      if (cacheConfig) {
        void writeCache(cacheConfig.cacheName, cacheKey, result);
      }
      if (syncCacheRef) {
        syncCacheRef.write(result);
      }
    } catch (error_) {
      if (fetchIdReference.current !== fetchId) return;
      const message = error_ instanceof Error ? error_.message : 'Unknown error';
      setError(message);
      // Keep stale data visible on error (SWR: don't clear data)
      if (!hadStaleData) {
        setDataState({
          data: null,
          source: 'none'
        });
        dataReference.current = {
          key: null,
          value: null
        };
      }
    } finally {
      if (fetchIdReference.current === fetchId) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [cacheKey, enabled]);
  useQueryRevalidation(enabled, revalidateOnFocus, doFetch);
  return {
    data: dataState.data,
    loading,
    error,
    refreshing,
    source: dataState.source,
    refetch: doFetch
  };
}